import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom';
import { makeStyles, createStyles, Theme, withStyles } from '@material-ui/core/styles';
import { 
    Divider, Card, LinearProgress, Breadcrumbs, Typography, CardHeader, CardContent,
    Table, TableContainer, TableRow, TableCell, TableHead, TableBody, Paper
} from "@material-ui/core";
import { Link } from 'react-router-dom';
import { Home, AccountBox } from "@material-ui/icons";
import moment from 'moment';

const useStyles = makeStyles((theme: Theme) => 
    createStyles({
        header: {
            display: "flex"
        },
        margin: {
            margin: theme.spacing(1),
        },
        progress: {
            backgroundColor: "#ff9800"
        },
        progressBackground: {
            backgroundColor: "#ffe0b2"
        },
        horizontalJustifiedLayout: {
            display: "flex",
            justifyContent: "space-between"
        },
        icon: {
            marginRight: theme.spacing(0.5),
            width: 20,
            height: 20,
        },
        link: {
            display: "flex"
        },
        table: {
            minWidth: 650,
        }
    })
);

const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    body: {
      fontSize: 14,
    },
  }),
)(TableCell);

const StyledTableRow = withStyles((theme: Theme) =>
  createStyles({
    root: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
      },
    },
  }),
)(TableRow);

interface ParamTypes {
    transactionId: string;
    customerId: string
}

interface InvoiceInfo {
    id: number;
    transactionTypeText: string;
    transactionDate: string;
    invoiceNo: string
    subTotal: number;
    taxTotal: number;
    amount: number;
    invoiceLines: Array<InvoiceLineInfo>;
}

interface InvoiceLineInfo {
    soLineId: number;
    stockcode: string;
    description: string;
    ordQuantity: number;
    invQuantity: number;
    unitPrice: number;
    lineTotal: number;
}

export default function InvoiceDetails() {
    const classes = useStyles()
    const { customerId, transactionId } = useParams()
    const [loading, setLoading] = useState<boolean>(true)
    const [invoiceInfo, setInvoiceInfo] = useState<InvoiceInfo | undefined>(undefined)

    useEffect(() => {
        const getInvoiceInfo = () => {
            setLoading(true)
            fetch(`/api/encore/invoices/${transactionId}`)
            .then(res => res.json())
            .then((result: InvoiceInfo) => {
                if(result) {
                    setInvoiceInfo(result)
                }
                setLoading(false)
            })
        }
        getInvoiceInfo()
            
    }, [transactionId, customerId])

    function formatDate(date: string) : moment.Moment | string {
        if (moment.parseZone(date).get('year') < 1900){
            return "";
        }
        return date ? moment.parseZone(date).format('DD MMM YYYY'): "";
    }

    function format2dp(n: number) {
        return n.toFixed(2);
    }

    return (
        <div>
            <Breadcrumbs>
                <Link to="/encore" className={classes.link}>
                    <Home className={classes.icon} />
                    Home
                </Link>
                <Link to="/encore/customers" className={classes.link}>
                    <AccountBox className={classes.icon} />
                    Customers
                </Link>
                <Link to={`/encore/customers/${customerId}`} className={classes.link}>
                    {customerId}
                </Link>
                <Typography color="textPrimary">Invoices</Typography>
                <Typography color="textPrimary">{transactionId}</Typography>
            </Breadcrumbs>
            <div className={classes.header}>
                <h2 style={{flexGrow: 1}}>
                    Invoice Details
                </h2>
            </div>
            <Divider />
            {loading && 
                <LinearProgress 
                    className={classes.progressBackground} 
                    classes={{barColorPrimary: classes.progress}}/>
            }
            {(!loading && invoiceInfo !== undefined) &&
                <div>
                    <div style={{marginTop: 8}}>
                        <Card>
                            <CardHeader title={`${invoiceInfo.id} - ${invoiceInfo.transactionTypeText}`}>
                            </CardHeader>
                            <CardContent>
                                <div>
                                    <span>Transaction Date: {formatDate(invoiceInfo.transactionDate)}</span>
                                </div>
                                <div>
                                    <span>Invoice No: {invoiceInfo.invoiceNo}</span>
                                </div>
                                <div className={classes.horizontalJustifiedLayout}>
                                    <span>Sub Total: {invoiceInfo.subTotal}</span>
                                    <span>Tax Total: {invoiceInfo.taxTotal}</span>
                                    <span>Total: {invoiceInfo.amount}</span>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    <div style={{marginTop: 16}}>
                        {(!loading && invoiceInfo.invoiceLines !== null) &&
                            <TableContainer component={Paper}>
                            <Table className={classes.table} size="small" aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <StyledTableCell>Stockcode</StyledTableCell>
                                        <StyledTableCell>Description</StyledTableCell>
                                        <StyledTableCell>Order Qty</StyledTableCell>
                                        <StyledTableCell>Invoice Qty</StyledTableCell>
                                        <StyledTableCell>Unit Price</StyledTableCell>
                                        <StyledTableCell>Line Total</StyledTableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                {invoiceInfo.invoiceLines.length !== 0 && invoiceInfo.invoiceLines.map((line: InvoiceLineInfo) => (
                                    <StyledTableRow key={line.soLineId}>
                                        <StyledTableCell component="th" scope="row">
                                                {line.stockcode}
                                        </StyledTableCell>
                                        <StyledTableCell>{line.description}</StyledTableCell>
                                        <StyledTableCell>{line.ordQuantity}</StyledTableCell>
                                        <StyledTableCell>{line.invQuantity}</StyledTableCell>
                                        <StyledTableCell>{format2dp(line.unitPrice)}</StyledTableCell>
                                        <StyledTableCell>{format2dp(line.lineTotal)}</StyledTableCell>
                                    </StyledTableRow>
                                ))}
                                </TableBody>
                            </Table>
                            </TableContainer>
                        }
                    </div>
                </div>
            }
        </div>
    )
}
